import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';

const FEATURES = [
  { label: 'AR Scans', free: '10 / month', premium: 'Unlimited' },
  { label: 'AI Insights', free: 'No', premium: 'Yes' },
  { label: 'Advanced Analytics', free: 'No', premium: 'Yes' },
  { label: 'Ad Experience', free: 'Ads', premium: 'Ad-Free' },
  { label: 'Budget Categories', free: '3', premium: 'Unlimited' },
  { label: 'Financial Goals', free: '2', premium: 'Unlimited' },
];

const MOCK_BILLING = [
  { id: '1', date: 'Mar 1, 2026', amount: '$9.99', status: 'Paid', invoice: '#INV-2026-03' },
  { id: '2', date: 'Feb 1, 2026', amount: '$9.99', status: 'Paid', invoice: '#INV-2026-02' },
  { id: '3', date: 'Jan 1, 2026', amount: '$9.99', status: 'Paid', invoice: '#INV-2026-01' },
  { id: '4', date: 'Dec 1, 2025', amount: '$9.99', status: 'Failed', invoice: '#INV-2025-12' },
];

const CANCEL_REASONS = [
  'Too Expensive',
  'Missing Features',
  'Not Using It',
  'Switching Apps',
  'Other',
];

export default function SubscriptionTab({ onToast }) {
  const { userProfile, updateProfile } = useAuth();
  const [plan, setPlan] = useState('free');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelFeedback, setCancelFeedback] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [upgrading, setUpgrading] = useState(false);

  useEffect(() => {
    if (userProfile?.plan) setPlan(userProfile?.plan);
  }, [userProfile]);

  const isPremium = plan === 'premium';

  const handleUpgrade = async () => {
    setUpgrading(true);
    try {
      await new Promise(r => setTimeout(r, 1000));
      const { error } = await updateProfile({ plan: 'premium' });
      if (error) throw error;
      setPlan('premium');
      onToast?.({ type: 'success', message: 'Upgraded to Premium! Welcome aboard 🎉' });
    } catch (err) {
      onToast?.({ type: 'error', message: err?.message || 'Upgrade failed. Please try again.' });
    } finally {
      setUpgrading(false);
    }
  };

  const handleCancelConfirm = async () => {
    if (!cancelReason) return onToast?.({ type: 'error', message: 'Please select a reason' });
    setCancelling(true);
    try {
      const { error } = await updateProfile({ plan: 'free' });
      if (error) throw error;
      setPlan('free');
      setShowCancelModal(false);
      setCancelReason('');
      setCancelFeedback('');
      onToast?.({ type: 'success', message: 'Subscription cancelled. You have been moved to the Free plan.' });
    } catch (err) {
      onToast?.({ type: 'error', message: err?.message || 'Cancellation failed' });
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Current Plan Card */}
      <div
        className="p-6 rounded-2xl relative overflow-hidden"
        style={{
          background: isPremium
            ? 'linear-gradient(135deg, rgba(123,47,190,0.2), rgba(0,229,255,0.1))'
            : 'rgba(255,255,255,0.04)',
          border: `1px solid ${isPremium ? 'rgba(123,47,190,0.4)' : 'rgba(255,255,255,0.08)'}`,
        }}
      >
        {isPremium && (
          <div className="absolute top-0 right-0 w-32 h-32 rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #7B2FBE, transparent)', transform: 'translate(30%, -30%)' }} />
        )}
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Icon name={isPremium ? 'Crown' : 'User'} size={20} color={isPremium ? '#C084FC' : '#64748B'} />
              <span
                className="text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider"
                style={isPremium ? { background: 'rgba(123,47,190,0.3)', color: '#C084FC' } : { background: 'rgba(255,255,255,0.08)', color: '#94A3B8' }}
              >
                {isPremium ? 'Premium' : 'Free'}
              </span>
            </div>
            <h3 className="text-xl font-bold text-white">{isPremium ? 'FinVisAR Premium' : 'FinVisAR Free'}</h3>
            <p className="text-sm mt-1" style={{ color: '#94A3B8' }}>
              {isPremium ? 'Next renewal: April 1, 2026 · $9.99/month' : 'Limited features — upgrade for full access'}
            </p>
          </div>
          {isPremium && (
            <div className="text-right">
              <p className="text-2xl font-bold" style={{ color: '#C084FC' }}>$9.99</p>
              <p className="text-xs" style={{ color: '#64748B' }}>/month</p>
            </div>
          )}
        </div>
      </div>
      {/* Feature Comparison */}
      <div>
        <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
          <Icon name="LayoutGrid" size={16} color="#00E5FF" />
          Plan Comparison
        </h3>
        <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
          <table className="w-full">
            <thead>
              <tr style={{ background: 'rgba(255,255,255,0.04)' }}>
                <th className="px-4 py-3 text-left text-xs font-medium" style={{ color: '#64748B' }}>Feature</th>
                <th className="px-4 py-3 text-center text-xs font-medium" style={{ color: '#94A3B8' }}>Free</th>
                <th className="px-4 py-3 text-center text-xs font-medium" style={{ color: '#C084FC' }}>Premium</th>
              </tr>
            </thead>
            <tbody>
              {FEATURES?.map((f, i) => (
                <tr key={f?.label} style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)', borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                  <td className="px-4 py-3 text-sm text-white">{f?.label}</td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-xs" style={{ color: f?.free === 'No' || f?.free === 'Ads' ? '#EF4444' : '#94A3B8' }}>{f?.free}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-xs font-medium" style={{ color: '#22C55E' }}>{f?.premium}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Free User: Upgrade CTA */}
      {!isPremium && (
        <div className="p-6 rounded-2xl text-center" style={{ background: 'linear-gradient(135deg, rgba(123,47,190,0.15), rgba(0,229,255,0.08))', border: '1px solid rgba(123,47,190,0.3)' }}>
          <Icon name="Crown" size={32} color="#C084FC" />
          <h3 className="text-lg font-bold text-white mt-3 mb-1">Upgrade to Premium</h3>
          <p className="text-sm mb-5" style={{ color: '#94A3B8' }}>Unlock unlimited AR scans, AI insights, advanced analytics, and more.</p>
          <button
            onClick={handleUpgrade}
            disabled={upgrading}
            className="inline-flex items-center gap-2 px-8 py-3 rounded-xl text-sm font-bold transition-all hover:scale-[1.03] disabled:opacity-60"
            style={{ background: 'linear-gradient(135deg, #7B2FBE, #9333EA)', color: 'white', boxShadow: '0 0 24px rgba(123,47,190,0.4)' }}
          >
            {upgrading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Icon name="Zap" size={16} color="white" />}
            {upgrading ? 'Processing...' : 'Upgrade Now — $9.99/month'}
          </button>
        </div>
      )}
      {/* Premium User: Billing History */}
      {isPremium && (
        <div>
          <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
            <Icon name="Receipt" size={16} color="#00E5FF" />
            Billing History
          </h3>
          <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
            <table className="w-full">
              <thead>
                <tr style={{ background: 'rgba(255,255,255,0.04)' }}>
                  {['Date', 'Amount', 'Status', 'Invoice']?.map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium" style={{ color: '#64748B' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {MOCK_BILLING?.map((b, i) => (
                  <tr key={b?.id} style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)', borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                    <td className="px-4 py-3 text-xs" style={{ color: '#94A3B8' }}>{b?.date}</td>
                    <td className="px-4 py-3 text-xs font-medium text-white">{b?.amount}</td>
                    <td className="px-4 py-3">
                      <span className="text-xs px-2 py-0.5 rounded-full" style={b?.status === 'Paid' ? { background: 'rgba(34,197,94,0.15)', color: '#22C55E' } : { background: 'rgba(239,68,68,0.15)', color: '#EF4444' }}>
                        {b?.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button className="text-xs flex items-center gap-1 hover:underline" style={{ color: '#00E5FF' }}>
                        <Icon name="Download" size={11} color="#00E5FF" />
                        {b?.invoice}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Cancel Subscription */}
          <div className="mt-6 p-4 rounded-xl" style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)' }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-white">Cancel Subscription</p>
                <p className="text-xs mt-0.5" style={{ color: '#64748B' }}>You will be moved to the Free plan at the end of your billing period.</p>
              </div>
              <button
                onClick={() => setShowCancelModal(true)}
                className="px-4 py-2 rounded-lg text-xs font-semibold transition-all hover:scale-[1.02]"
                style={{ background: 'rgba(239,68,68,0.15)', color: '#EF4444', border: '1px solid rgba(239,68,68,0.3)' }}
              >
                Cancel Plan
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Reactivate CTA for cancelled users */}
      {!isPremium && userProfile?.plan_cancelled_at && (
        <div className="p-4 rounded-xl flex items-center justify-between" style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)' }}>
          <div>
            <p className="text-sm font-medium text-white">Reactivate Premium</p>
            <p className="text-xs mt-0.5" style={{ color: '#94A3B8' }}>Welcome back! Reactivate to restore all premium features.</p>
          </div>
          <button onClick={handleUpgrade} className="px-4 py-2 rounded-lg text-xs font-semibold" style={{ background: 'rgba(245,158,11,0.2)', color: '#F59E0B', border: '1px solid rgba(245,158,11,0.3)' }}>
            Reactivate
          </button>
        </div>
      )}
      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)' }}>
          <div className="w-full max-w-md p-6 rounded-2xl" style={{ background: '#0D1B2A', border: '1px solid rgba(239,68,68,0.25)' }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(239,68,68,0.15)' }}>
                <Icon name="AlertTriangle" size={18} color="#EF4444" />
              </div>
              <h3 className="text-lg font-semibold text-white">Cancel Subscription</h3>
            </div>
            <p className="text-sm mb-5" style={{ color: '#94A3B8' }}>We are sorry to see you go. Please tell us why you are cancelling.</p>
            <div className="space-y-2 mb-4">
              {CANCEL_REASONS?.map(reason => (
                <button
                  key={reason}
                  onClick={() => setCancelReason(reason)}
                  className="w-full text-left px-4 py-2.5 rounded-xl text-sm transition-all"
                  style={{
                    background: cancelReason === reason ? 'rgba(239,68,68,0.15)' : 'rgba(255,255,255,0.04)',
                    border: `1px solid ${cancelReason === reason ? 'rgba(239,68,68,0.4)' : 'rgba(255,255,255,0.06)'}`,
                    color: cancelReason === reason ? '#EF4444' : '#94A3B8',
                  }}
                >
                  {reason}
                </button>
              ))}
            </div>
            <textarea
              value={cancelFeedback}
              onChange={e => setCancelFeedback(e?.target?.value)}
              placeholder="Additional feedback (optional)"
              rows={3}
              className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-[#475569] outline-none focus:ring-2 focus:ring-[#EF4444] resize-none mb-4"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
            />
            <div className="flex gap-3">
              <button onClick={() => setShowCancelModal(false)} className="flex-1 py-2.5 rounded-xl text-sm font-medium" style={{ background: 'rgba(255,255,255,0.06)', color: '#94A3B8', border: '1px solid rgba(255,255,255,0.08)' }}>Keep Premium</button>
              <button
                onClick={handleCancelConfirm}
                disabled={cancelling || !cancelReason}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all disabled:opacity-50"
                style={{ background: 'rgba(239,68,68,0.2)', color: '#EF4444', border: '1px solid rgba(239,68,68,0.3)' }}
              >
                {cancelling ? 'Cancelling...' : 'Confirm Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
